import { useDialog } from 'primevue/usedialog'
import type {
    DynamicDialogInstance,
    DynamicDialogOptions,
    DynamicDialogTemplates
} from 'primevue/dynamicdialogoptions'
import { cloneVNode, computed, defineComponent, h, isVNode, markRaw } from 'vue'
import type { Component } from 'vue'
import { useI18n } from 'vue-i18n'
import { useScopedGlobalI18n } from '~/composables/use-scoped-global-i18n'

type DynamicDialogContent = unknown
type LocaleRef = {
    readonly value: string
}

export function useScopedDialog(): {
    open: (
        content: DynamicDialogContent,
        options?: DynamicDialogOptions
    ) => DynamicDialogInstance
} {
    const dialog = useDialog()
    const { locale } = useI18n({ useScope: 'global' })

    return {
        open(content, options) {
            return dialog.open(
                createScopedDialogContent(content, locale),
                createScopedDialogOptions(options, locale)
            )
        }
    }
}

function createScopedDialogOptions(
    options: DynamicDialogOptions | undefined,
    locale: LocaleRef
): DynamicDialogOptions | undefined {
    if (options === undefined) {
        return undefined
    }

    return {
        ...options,
        templates: createScopedDialogTemplates(options.templates, locale)
    }
}

function createScopedDialogTemplates(
    templates: DynamicDialogTemplates | undefined,
    locale: LocaleRef
): DynamicDialogTemplates | undefined {
    if (templates === undefined) {
        return undefined
    }

    return {
        ...templates,
        header: createScopedDialogTemplate(templates.header, locale),
        footer: createScopedDialogTemplate(templates.footer, locale)
    }
}

function createScopedDialogTemplate(
    template: unknown,
    locale: LocaleRef
): unknown {
    if (Array.isArray(template)) {
        return template.map(templateItem =>
            createScopedDialogContent(templateItem, locale)
        )
    }

    if (template === undefined || template === null) {
        return undefined
    }

    return createScopedDialogContent(template, locale)
}

function createScopedDialogContent(
    content: DynamicDialogContent,
    locale: LocaleRef
) {
    return markRaw(
        defineComponent({
            name: 'ScopedDynamicDialogContent',
            inheritAttrs: false,
            setup(_, { attrs }) {
                // PrimeVue DynamicDialog mounts content under the app-level
                // host, so re-create the caller's i18n scope before rendering
                // the real modal component.
                useScopedGlobalI18n(
                    computed(() => {
                        return locale.value
                    })
                )

                return () => {
                    if (isVNode(content)) {
                        return cloneVNode(content, attrs)
                    }

                    return h(content as Component, attrs)
                }
            }
        })
    )
}
